import axios from 'axios';
import JSZip from 'jszip';
import dbUrl, { PRODUCTION_DB_URL, STAGING_DB_URL, isStagingDb } from '../utils/credentials';

const REQUEST_TIMEOUT_MS = 120000;
const REQUIRED_ANY_NODE = ['events', 'invitations'];

export const CURRENT_DB_LABEL = isStagingDb ? 'collaudo' : 'produzione';

function pad(n) {
  return String(n).padStart(2, '0');
}

export async function fetchDatabase(baseUrl = dbUrl) {
  const res = await axios.get(`${baseUrl}.json`, { timeout: REQUEST_TIMEOUT_MS });
  return res.data;
}

export function summarizeDatabase(data) {
  return Object.entries(data || {}).map(([node, value]) => ({
    node,
    count: value && typeof value === 'object' ? Object.keys(value).length : 1,
  }));
}

function assertValidSnapshot(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Il file non contiene un database valido (atteso un oggetto JSON).');
  }
  if (!REQUIRED_ANY_NODE.some((key) => data[key] && typeof data[key] === 'object')) {
    throw new Error('Il file non sembra un backup di questo database (mancano "events" e "invitations").');
  }
}

export function downloadBackup(data, label = CURRENT_DB_LABEL) {
  const now = new Date();
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const blob = new Blob([JSON.stringify(data ?? {}, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `backup-${label}-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function parseBackupFile(file) {
  const name = file.name.toLowerCase();
  let text;

  if (name.endsWith('.json')) {
    text = await file.text();
  } else if (name.endsWith('.zip')) {
    const zip = await JSZip.loadAsync(file);
    const entry = Object.values(zip.files).find(
      (f) => !f.dir && f.name.toLowerCase().endsWith('.json') && !f.name.startsWith('__MACOSX')
    );
    if (!entry) throw new Error('Lo zip non contiene alcun file .json.');
    text = await entry.async('string');
  } else if (name.endsWith('.rar')) {
    throw new Error('I file .rar non sono supportati: estrai il .json e caricalo, oppure usa uno .zip.');
  } else {
    throw new Error('Formato non supportato: usa un file .json o .zip.');
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Il file non è un JSON valido.');
  }
  assertValidSnapshot(data);
  return data;
}

// Sostituisce l'intero database con PUT sulla radice (atomico) e verifica i nodi scritti.
export async function replaceDatabase(baseUrl, data) {
  assertValidSnapshot(data);
  await axios.put(`${baseUrl}.json`, data, { timeout: REQUEST_TIMEOUT_MS });
  const check = await axios.get(`${baseUrl}.json?shallow=true`, { timeout: REQUEST_TIMEOUT_MS });
  const written = Object.keys(check.data || {}).sort().join(',');
  const expected = Object.keys(data).sort().join(',');
  if (written !== expected) {
    throw new Error('Verifica fallita: i nodi scritti non corrispondono al backup.');
  }
}

// La destinazione è fissata a STAGING_DB_URL: la produzione viene solo letta.
export async function copyProductionToStaging() {
  if (!isStagingDb) throw new Error('Operazione consentita solo in ambiente di collaudo.');
  const data = await fetchDatabase(PRODUCTION_DB_URL);
  assertValidSnapshot(data);
  await replaceDatabase(STAGING_DB_URL, data);
  return summarizeDatabase(data);
}
